"use client";

import { DocumentItem } from "@/components/app/documents/document-item";
import { DocumentSelectorDialog } from "@/components/app/documents/document-selector-dialog";
import { useToast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";
import { uploadFile } from "@/lib/storage";
import type {
  Document,
  DocumentCategory,
  DocumentType,
  uploadSchema,
} from "@/schemas/document";
import { AlertCircle, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import type { z } from "zod";

interface RequiredDocument {
  id: string;
  title: string;
  description: string;
  required: boolean;
  category: DocumentCategory;
  type: DocumentType;
  priority: "high" | "medium";
  conditional?: string;
  status?: "pending" | "completed";
  expectedFrom?: string;
}

const REQUIRED_DOCUMENTS: RequiredDocument[] = [
  {
    id: "purchase_contract",
    title: "Purchase Contract",
    description:
      "Your signed purchase agreement, which title company needs to begin title work",
    required: true,
    category: "purchase_agreement",
    type: "purchase_contract",
    priority: "high",
    expectedFrom: "Seller's Agent",
  },
  {
    id: "earnest_money",
    title: "Earnest Money Receipt",
    description: "Proof of earnest money deposit to title company or escrow",
    required: true,
    category: "offer",
    type: "earnest_money_receipt",
    priority: "high",
    expectedFrom: "Title Company",
  },
  {
    id: "title_commitment",
    title: "Title Commitment",
    description:
      "Report from title company showing property ownership and any issues to resolve",
    required: true,
    category: "title",
    type: "title_commitment",
    priority: "high",
    expectedFrom: "Title Company",
  },
  {
    id: "tax_certificate",
    title: "Property Tax Certificate",
    description:
      "Official document showing current tax status and any outstanding taxes",
    required: true,
    category: "title",
    type: "tax_certificate",
    priority: "high",
    expectedFrom: "Title Company",
  },
  {
    id: "proof_of_funds",
    title: "Proof of Funds",
    description:
      "Bank statements or investment accounts showing available funds for purchase",
    required: true,
    category: "proof_of_funds",
    type: "proof_of_funds_letter",
    priority: "high",
  },
  {
    id: "loan_commitment",
    title: "Loan Commitment",
    description: "Final approval from lender confirming mortgage terms",
    required: true,
    category: "mortgage",
    type: "loan_commitment",
    priority: "high",
    conditional: "If financing with a mortgage",
    expectedFrom: "Lender",
  },
  {
    id: "entity_docs",
    title: "Entity Documents",
    description:
      "Legal documents proving authority to buy property under business/trust name",
    required: false,
    category: "entity",
    type: "operating_agreement",
    priority: "medium",
    conditional: "If buying as LLC/Corp/Trust",
  },
  {
    id: "hoa_docs",
    title: "HOA Documents",
    description:
      "Current HOA bylaws, financials and rules provided by seller or HOA",
    required: false,
    category: "hoa",
    type: "hoa_bylaws",
    priority: "medium",
    conditional: "If property is part of an HOA",
    expectedFrom: "HOA Management",
  },
  {
    id: "closing_statement",
    title: "Closing Statement",
    description:
      "Final document showing all costs, credits, and prorations for closing",
    required: true,
    category: "closing",
    type: "closing_statement",
    priority: "high",
  },
];

interface DocumentSectionProps {
  title: string;
  icon: React.ReactNode;
  documents: RequiredDocument[];
  documentSelections: Record<string, Document>;
  onSelect: (doc: RequiredDocument) => void;
  onDelete: (doc: RequiredDocument) => void;
}

function DocumentSection({
  title,
  icon,
  documents,
  documentSelections,
  onSelect,
  onDelete,
}: DocumentSectionProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {icon}
        <h4 className="font-semibold text-sm">{title}</h4>
      </div>
      <div className="space-y-3">
        {documents.map((doc) => (
          <DocumentItem
            key={doc.id}
            doc={doc}
            selectedDocument={documentSelections[doc.id]}
            onSelect={() => onSelect(doc)}
            onDelete={() => onDelete(doc)}
          />
        ))}
      </div>
    </div>
  );
}

export function DocumentChecklist() {
  const {
    createDocument,
    addBuyerDocument,
    deleteBuyerDocument,
    updateBuyerDocument,
    journeyData,
    journeyDocuments,
    userProfile,
    documents,
  } = useJourneyData();
  const [activeDocumentSelection, setActiveDocumentSelection] =
    useState<RequiredDocument | null>(null);
  const [documentSelections, setDocumentSelections] = useState<
    Record<string, Document>
  >({});
  const { toast } = useToast();

  if (!journeyData?.id) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto space-y-6">
          <div className="bg-primary/5 rounded-2xl p-8 mx-auto">
            <div className="bg-primary/10 rounded-full p-5 w-20 h-20 mx-auto flex items-center justify-center">
              <FileText className="w-10 h-10 text-primary" />
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">
              No Active Journey
            </h3>
            <p className="text-gray-600 max-w-sm mx-auto">
              You need to create or select a journey before you can manage
              documents.
            </p>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (!journeyDocuments?.length || !documents?.length) return;

    const selections: Record<string, Document> = {};
    for (const requiredDoc of REQUIRED_DOCUMENTS) {
      const referenceId = `${requiredDoc.category}-${requiredDoc.type}`;
      const journeyDoc = journeyDocuments.find(
        (bd) => bd.reference_id === referenceId,
      );
      if (journeyDoc) {
        const doc = documents.find((d) => d.id === journeyDoc.document.id);
        if (doc) selections[requiredDoc.id] = doc;
      }
    }
    setDocumentSelections(selections);
  }, [journeyDocuments, documents]);

  const handleDocumentSelect = async (doc: Document | null) => {
    if (!doc || !activeDocumentSelection || !userProfile?.id) return;

    try {
      const referenceId = `${activeDocumentSelection.category}-${activeDocumentSelection.type}`;
      const existingJourneyDoc = journeyDocuments.find(
        (bd) => bd.reference_id === referenceId,
      );

      // Close the dialog
      setActiveDocumentSelection(null);

      if (existingJourneyDoc) {
        await updateBuyerDocument.mutateAsync({
          id: existingJourneyDoc.id,
          document_id: doc.id,
        });
      } else {
        await addBuyerDocument.mutateAsync({
          document_id: doc.id,
          journey_id: journeyData?.id ?? "",
          user_id: userProfile.id,
          reference_id: referenceId,
        });
      }
    } catch (error) {
      console.error("Failed to update document selection:", error);
      toast({
        title: "Error",
        description: "Failed to update document selection",
        variant: "destructive",
      });
    }
  };

  const handleUpload = async (formData: z.infer<typeof uploadSchema>) => {
    if (!userProfile?.id) throw new Error("No user ID found");

    try {
      const fileName = `${userProfile.id}/documents/${Date.now()}_${formData.file.name}`;
      const { url, path } = await uploadFile(
        "documents",
        fileName,
        formData.file,
      );

      const newDocument: Document | null = await createDocument.mutateAsync({
        type: formData.type,
        category: formData.category,
        name: formData.file.name,
        url,
        file_type: formData.file.type,
        file_path: path,
        size_bytes: formData.file.size,
      });
      if (newDocument) {
        handleDocumentSelect(newDocument);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        title: "Upload Failed",
        description:
          error instanceof Error ? error.message : "Failed to upload file",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDelete = async (doc: RequiredDocument) => {
    const referenceId = `${doc.category}-${doc.type}`;
    const journeyDoc = journeyDocuments.find(
      (bd) => bd.reference_id === referenceId,
    );

    if (journeyDoc) {
      try {
        await deleteBuyerDocument.mutateAsync(journeyDoc.id);
        setDocumentSelections((prev) => {
          const updated = { ...prev };
          delete updated[doc.id];
          return updated;
        });
      } catch (error) {
        console.error("Failed to delete document:", error);
        toast({
          title: "Error",
          description: "Failed to delete document",
          variant: "destructive",
        });
      }
    }
  };

  const groupedDocuments = {
    high: REQUIRED_DOCUMENTS.filter((doc) => doc.priority === "high"),
    medium: REQUIRED_DOCUMENTS.filter((doc) => doc.priority === "medium"),
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold">Required Documents</h3>
        <p className="text-sm text-muted-foreground">
          Upload or select the necessary documents for your due diligence review
        </p>
      </div>

      <DocumentSection
        title="Required Documents"
        icon={<AlertCircle className="h-4 w-4 text-red-500" />}
        documents={groupedDocuments.high}
        documentSelections={documentSelections}
        onSelect={setActiveDocumentSelection}
        onDelete={handleDelete}
      />

      <DocumentSection
        title="Additional Documents"
        icon={<FileText className="h-4 w-4 text-muted-foreground" />}
        documents={groupedDocuments.medium}
        documentSelections={documentSelections}
        onSelect={setActiveDocumentSelection}
        onDelete={handleDelete}
      />

      {activeDocumentSelection && (
        <DocumentSelectorDialog
          open={!!activeDocumentSelection}
          onOpenChange={(open) => !open && setActiveDocumentSelection(null)}
          onSelect={handleDocumentSelect}
          documents={documents.filter(
            (doc) =>
              doc.category === activeDocumentSelection.category &&
              doc.type === activeDocumentSelection.type,
          )}
          selectedDocument={documentSelections[activeDocumentSelection.id]}
          category={activeDocumentSelection.category}
          type={activeDocumentSelection.type}
          title={`Add ${activeDocumentSelection.title}`}
          description="Select an existing document or upload a new one"
          onUpload={handleUpload}
          strictType={true}
        />
      )}
    </div>
  );
}
